/**
 * Merge STANDINGS_UPDATED AppSync payload into a cached GET /standings response.
 * Preserves user-specific coin fields (isMember, userBalance, canJoin, etc.).
 */

const USER_COIN_KEYS = [
  'isMember',
  'userBalance',
  'ageRestriction',
  'hasRoundBets',
  'canJoin',
  'roundHasStarted',
  'roundInfo'
];

function roundCodeOf(roundPayload) {
  return roundPayload?.round?.roundCode || null;
}

function coinRoundCode(coin) {
  return coin?.roundCode || null;
}

function patchRoundSlot(existing, eventRound) {
  if (!existing || !eventRound?.roundCode) return existing;
  if (roundCodeOf(existing) !== eventRound.roundCode) return existing;
  return {
    ...existing,
    standings: Array.isArray(eventRound.standings) ? eventRound.standings : existing.standings,
    roundStats:
      eventRound.roundStats !== undefined ? eventRound.roundStats : existing.roundStats
  };
}

function patchCoinSlot(existingCoin, eventCoin) {
  if (!existingCoin || !eventCoin?.roundCode) return existingCoin;
  if (coinRoundCode(existingCoin) !== eventCoin.roundCode) return existingCoin;
  const next = {
    ...existingCoin,
    standings: Array.isArray(eventCoin.standings) ? eventCoin.standings : existingCoin.standings,
    entryFee: eventCoin.entryFee !== undefined ? eventCoin.entryFee : existingCoin.entryFee,
    totalCoinsPrize:
      eventCoin.totalCoinsPrize !== undefined
        ? eventCoin.totalCoinsPrize
        : existingCoin.totalCoinsPrize,
    leagueId: eventCoin.leagueId || existingCoin.leagueId
  };
  for (const key of USER_COIN_KEYS) {
    if (existingCoin[key] !== undefined) next[key] = existingCoin[key];
  }
  return next;
}

/**
 * @param {object} standingsData - cached GET /standings body
 * @param {object} event - STANDINGS_UPDATED event
 * @returns {object | null} merged data, or null if event does not apply
 */
export function mergeStandingsUpdated(standingsData, event) {
  if (!standingsData || !event || event.type !== 'STANDINGS_UPDATED') return null;
  if (event.leagueCode && standingsData.leagueCode && event.leagueCode !== standingsData.leagueCode) {
    return null;
  }

  const next = {
    ...standingsData,
    processingMatch: null,
    season: event.season
      ? { ...standingsData.season, ...event.season }
      : standingsData.season,
    previousRound: patchRoundSlot(standingsData.previousRound, event.round),
    currentRound: patchRoundSlot(standingsData.currentRound, event.round),
    nextRound: patchRoundSlot(standingsData.nextRound, event.round),
    previousRoundCoin: patchCoinSlot(standingsData.previousRoundCoin, event.coin),
    currentRoundCoin: patchCoinSlot(standingsData.currentRoundCoin, event.coin),
    nextRoundCoin: patchCoinSlot(standingsData.nextRoundCoin, event.coin)
  };
  return next;
}

/**
 * Banner label for a processing match summary.
 * @param {object | null | undefined} match
 */
export function formatProcessingMatchBanner(match) {
  if (!match) return null;
  if (match.voidResult) {
    const home = match.homeTeamTla || match.homeTeam || 'Home';
    const away = match.awayTeamTla || match.awayTeam || 'Away';
    return `Processing postponed/canceled ${home} vs ${away} — updating standings…`;
  }
  const home = match.homeTeam || match.homeTeamTla || 'Home';
  const away = match.awayTeam || match.awayTeamTla || 'Away';
  const hs = match.homeScore != null ? match.homeScore : '–';
  const as = match.awayScore != null ? match.awayScore : '–';
  return `Processing ${home} ${hs}–${as} ${away} — updating standings…`;
}
